import { useCallback, useEffect, useMemo, useState } from "react";
import { Banner, BlockStack, Button, Card, DataTable, InlineStack, Select, Text, TextField } from "@shopify/polaris";
import { useNavigate, useOutletContext } from "react-router-dom";
import agent from "../../app/api/agent";
import { AdminActivityDashboard, AdminStorefrontTopSearch } from "../../app/models/Admin";
import { formatLocalDateTime } from "./utils/dateTime";

type AdminConsoleContext = {
  token: string;
  onUnauthorized: () => void;
};

const dayOptions = [
  { label: "Last 7 days", value: "7" },
  { label: "Last 30 days", value: "30" },
  { label: "Last 90 days", value: "90" },
  { label: "Last 365 days", value: "365" },
];

const formatNumber = (value?: number | null) => {
  return `${value ?? 0}`;
};

const formatDateOnly = (value?: string) => {
  if (!value) return "-";
  return value.slice(0, 10);
};

const searchLabel = (search: AdminStorefrontTopSearch) => {
  const explicit = search.vehicleLabel?.trim();
  if (explicit) return explicit;

  return [search.year, search.make, search.model, search.submodel].filter(Boolean).join(" ") || "-";
};

export default function AdminActivityPage() {
  const { token, onUnauthorized } = useOutletContext<AdminConsoleContext>();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<AdminActivityDashboard | null>(null);
  const [days, setDays] = useState(30);
  const [shopIdInput, setShopIdInput] = useState("");
  const [activeShopId, setActiveShopId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = useCallback(async (daysToLoad = days, shopIdToLoad = activeShopId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await agent.Admin.activity(token, daysToLoad, shopIdToLoad);
      setDashboard(response);
    } catch {
      setError("Unable to load storefront activity.");
      onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [activeShopId, days, onUnauthorized, token]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const latestDailyCounts = useMemo(() => {
    return [...(dashboard?.dailyCounts ?? [])].slice(-14).reverse();
  }, [dashboard]);

  const applyFilters = () => {
    const trimmedShopId = shopIdInput.trim();
    const parsedShopId = trimmedShopId ? Number.parseInt(trimmedShopId, 10) : undefined;
    const nextShopId = Number.isFinite(parsedShopId) && (parsedShopId ?? 0) > 0 ? parsedShopId : undefined;

    setActiveShopId(nextShopId);
    void loadDashboard(days, nextShopId);
  };

  const clearShopFilter = () => {
    setShopIdInput("");
    setActiveShopId(undefined);
    void loadDashboard(days, undefined);
  };

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between" blockAlign="center">
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">Storefront activity</Text>
          <Text as="p" tone="subdued">Vehicle search usage across merchant storefronts.</Text>
        </BlockStack>
        <Button onClick={() => loadDashboard()} loading={loading}>Refresh</Button>
      </InlineStack>

      {error ? <Banner tone="critical">{error}</Banner> : null}

      <Card>
        <InlineStack gap="300" blockAlign="end" align="space-between">
          <InlineStack gap="300" blockAlign="end">
            <div style={{ width: "180px" }}>
              <Select
                label="Range"
                options={dayOptions}
                value={String(days)}
                onChange={(value) => {
                  const nextDays = Number.parseInt(value, 10);
                  setDays(nextDays);
                  void loadDashboard(nextDays, activeShopId);
                }}
              />
            </div>
            <div style={{ width: "180px" }}>
              <TextField
                label="Shop ID"
                type="number"
                autoComplete="off"
                placeholder="All shops"
                value={shopIdInput}
                onChange={setShopIdInput}
              />
            </div>
            <Button onClick={applyFilters} loading={loading}>Apply</Button>
            <Button onClick={clearShopFilter} disabled={loading && !activeShopId}>Clear shop</Button>
          </InlineStack>
          <Text as="span" tone="subdued">Generated: {formatLocalDateTime(dashboard?.generatedAt)}</Text>
        </InlineStack>
      </Card>

      <InlineStack gap="300" align="start">
        <Card>
          <BlockStack gap="100">
            <Text as="p" variant="bodySm" tone="subdued">Total searches</Text>
            <Text as="p" variant="headingLg">{formatNumber(dashboard?.totalSearches)}</Text>
          </BlockStack>
        </Card>
        <Card>
          <BlockStack gap="100">
            <Text as="p" variant="bodySm" tone="subdued">Today</Text>
            <Text as="p" variant="headingLg">{formatNumber(dashboard?.searchesToday)}</Text>
          </BlockStack>
        </Card>
        <Card>
          <BlockStack gap="100">
            <Text as="p" variant="bodySm" tone="subdued">Active shops</Text>
            <Text as="p" variant="headingLg">{formatNumber(dashboard?.activeShopCount)}</Text>
          </BlockStack>
        </Card>
        <Card>
          <BlockStack gap="100">
            <Text as="p" variant="bodySm" tone="subdued">Unique searches</Text>
            <Text as="p" variant="headingLg">{formatNumber(dashboard?.uniqueSearchCount)}</Text>
          </BlockStack>
        </Card>
      </InlineStack>

      <Card>
        <BlockStack gap="200">
          <Text as="h3" variant="headingMd">Top vehicle searches</Text>
          {(dashboard?.topSearches ?? []).length === 0 ? (
            <Text as="p">No search analytics recorded yet.</Text>
          ) : (
            <DataTable
              columnContentTypes={["text", "numeric", "numeric", "text", "text"]}
              headings={["Search", "Searches", "Shops", "Last searched", "Fields"]}
              rows={(dashboard?.topSearches ?? []).map((item) => [
                searchLabel(item),
                formatNumber(item.searchCount),
                formatNumber(item.shopCount),
                formatLocalDateTime(item.lastSearchedAt),
                [item.year, item.make, item.model, item.submodel].filter(Boolean).join(" / ") || "-"
              ])}
            />
          )}
        </BlockStack>
      </Card>

      <InlineStack gap="300" align="start">
        <div style={{ flex: 1, minWidth: "360px" }}>
          <Card>
            <BlockStack gap="200">
              <Text as="h3" variant="headingMd">Top stores</Text>
              {(dashboard?.topStores ?? []).length === 0 ? (
                <Text as="p">No store activity in this range.</Text>
              ) : (
                <DataTable
                  columnContentTypes={["text", "numeric", "numeric", "text"]}
                  headings={["Store", "Searches", "Unique", "Last searched"]}
                  rows={(dashboard?.topStores ?? []).map((store) => [
                    <Button key={`store-${store.shopId}`} variant="plain" onClick={() => navigate(`/pf-console/stores/${store.shopId}`)}>
                      {store.shopName || store.shopDomain || `Shop ${store.shopId}`}
                    </Button>,
                    formatNumber(store.searchCount),
                    formatNumber(store.uniqueSearchCount),
                    formatLocalDateTime(store.lastSearchedAt)
                  ])}
                />
              )}
            </BlockStack>
          </Card>
        </div>
        <div style={{ flex: 1, minWidth: "360px" }}>
          <Card>
            <BlockStack gap="200">
              <Text as="h3" variant="headingMd">Top makes and models</Text>
              {(dashboard?.topModels ?? []).length === 0 ? (
                <Text as="p">No model activity in this range.</Text>
              ) : (
                <DataTable
                  columnContentTypes={["text", "numeric", "text"]}
                  headings={["Vehicle", "Searches", "Last searched"]}
                  rows={(dashboard?.topModels ?? []).map((model) => [
                    [model.make, model.model].filter(Boolean).join(" ") || "-",
                    formatNumber(model.searchCount),
                    formatLocalDateTime(model.lastSearchedAt)
                  ])}
                />
              )}
            </BlockStack>
          </Card>
        </div>
      </InlineStack>

      <InlineStack gap="300" align="start">
        <div style={{ flex: 1, minWidth: "360px" }}>
          <Card>
            <BlockStack gap="200">
              <Text as="h3" variant="headingMd">Daily search volume</Text>
              {latestDailyCounts.length === 0 ? (
                <Text as="p">No daily activity in this range.</Text>
              ) : (
                <DataTable
                  columnContentTypes={["text", "numeric", "numeric"]}
                  headings={["Date", "Searches", "Shops"]}
                  rows={latestDailyCounts.map((item) => [
                    formatDateOnly(item.date),
                    formatNumber(item.searchCount),
                    formatNumber(item.shopCount)
                  ])}
                />
              )}
            </BlockStack>
          </Card>
        </div>
        <div style={{ flex: 1, minWidth: "360px" }}>
          <Card>
            <BlockStack gap="200">
              <Text as="h3" variant="headingMd">Top makes</Text>
              {(dashboard?.topMakes ?? []).length === 0 ? (
                <Text as="p">No make activity in this range.</Text>
              ) : (
                <DataTable
                  columnContentTypes={["text", "numeric", "numeric", "text"]}
                  headings={["Make", "Searches", "Models", "Last searched"]}
                  rows={(dashboard?.topMakes ?? []).map((make) => [
                    make.make || "-",
                    formatNumber(make.searchCount),
                    formatNumber(make.modelCount),
                    formatLocalDateTime(make.lastSearchedAt)
                  ])}
                />
              )}
            </BlockStack>
          </Card>
        </div>
      </InlineStack>
    </BlockStack>
  );
}
